import { differenceInCalendarDays, isValid, parseISO } from 'date-fns';

import { RHI_V2_KYC_NEXT_STEP } from '@/constants/healthInsuranceConfig';
import { RHI_SESSION_KEYS } from '@/constants/queryKeys';
import { IRhiV2Journey, TRhiV2JourneyStage } from '@/types/rhiV2';
import { formatSumInsured } from '@/utils/rhiV2/plans';

export const HEALTH_INSURANCE_BASE_PATH = '/retail/health-insurance';

export const RHI_V2_ERROR_PATH = `${HEALTH_INSURANCE_BASE_PATH}/error`;

export const RHI_V2_NEW_QUOTE_PATH = `${HEALTH_INSURANCE_BASE_PATH}/select-member`;

export const RHI_V2_PLANS_PATH = `${HEALTH_INSURANCE_BASE_PATH}/plans`;

/** Where an agent lands after redeem: the customer's journeys and Start new quote. */
export const RHI_V2_AGENT_PATH = `${HEALTH_INSURANCE_BASE_PATH}/agent`;

export const RHI_V2_AGENT_NEW_QUOTE_PATH = `${RHI_V2_AGENT_PATH}/new-quote`;

/** The RHI v2 screens addressed by plan id. */
export const rhiV2Paths = {
  plan: (planId: string) => `${RHI_V2_PLANS_PATH}/${planId}`,
  proposal: (planId: string) => `${HEALTH_INSURANCE_BASE_PATH}/proposal/${planId}`,
  proposalReview: (planId: string) =>
    `${HEALTH_INSURANCE_BASE_PATH}/proposal/${planId}/review`,
  payment: (planId: string, quoteId?: string) =>
    `${HEALTH_INSURANCE_BASE_PATH}/payment/${planId}${
      quoteId ? `?quoteId=${encodeURIComponent(quoteId)}` : ''
    }`,
  policy: (planId: string, quoteId: string) =>
    `${HEALTH_INSURANCE_BASE_PATH}/policy/${planId}?quoteId=${quoteId}`,
  success: (planId: string, quoteId: string) =>
    `${HEALTH_INSURANCE_BASE_PATH}/success?quoteId=${quoteId}&planId=${planId}`,
};

export const rhiV2KycPath = (planId: string, quoteId: string) =>
  `${HEALTH_INSURANCE_BASE_PATH}/kyc/${planId}?quoteId=${encodeURIComponent(quoteId)}`;

/**
 * The screen a committed plan goes to, from `kyc.nextStep` alone. The KYC page shows the form,
 * OTP, processing or price step from its own state. Anything else (no block, or a step this
 * build doesn't know) goes to the KYC page too, which asks the server again; never straight
 * to the proposal.
 */
export const rhiV2NextStepPath = (
  nextStep: string | undefined,
  planId: string,
  quoteId: string,
) =>
  nextStep === RHI_V2_KYC_NEXT_STEP.PROPOSAL
    ? rhiV2Paths.proposal(planId)
    : rhiV2KycPath(planId, quoteId);

export const rhiV2KycLink = (planId: string, quoteId: string) =>
  `${window.location.origin}${rhiV2KycPath(planId, quoteId)}`;

export type TRhiV2JourneyTarget =
  | 'plans'
  | 'planSelection'
  | 'kyc'
  | 'proposal'
  | 'proposalReview'
  | 'payment'
  | 'paymentStatus'
  | 'policy'
  | 'support'
  | 'newQuote';

interface IRhiV2JourneyStageConfig {
  title: string;
  cta: string;
  target: TRhiV2JourneyTarget;
}

const CONTINUE = 'Continue';

export const RHI_V2_JOURNEY_STAGES: Record<
  TRhiV2JourneyStage,
  IRhiV2JourneyStageConfig
> = {
  POLICY_ISSUED: {
    title: 'Policy Issued',
    cta: 'View',
    target: 'policy',
  },
  POLICY_IN_PROGRESS: {
    title: 'Policy is being issued',
    cta: 'View',
    target: 'paymentStatus',
  },
  POLICY_ATTENTION: {
    title: 'This policy needs attention',
    cta: 'Chat with us',
    target: 'support',
  },
  PAYMENT_SUCCEEDED: {
    title: 'Payment successful',
    cta: 'View',
    target: 'paymentStatus',
  },
  PAYMENT_IN_PROGRESS: {
    title: 'A payment is already in progress',
    cta: CONTINUE,
    target: 'paymentStatus',
  },
  PAYMENT_UNCONFIRMED: {
    title: "We're confirming your payment.",
    cta: CONTINUE,
    target: 'paymentStatus',
  },
  PAYMENT_FAILED: {
    title: 'Payment failed',
    cta: CONTINUE,
    target: 'payment',
  },
  PROPOSAL_SUBMITTED: {
    title: 'Proposal submitted',
    cta: CONTINUE,
    target: 'payment',
  },
  PROPOSAL_AFFIRMED: {
    title: "You've confirmed these details",
    cta: CONTINUE,
    target: 'proposalReview',
  },
  PROPOSAL_AWAITING_CUSTOMER: {
    title: 'Action needed',
    cta: CONTINUE,
    target: 'proposalReview',
  },
  PROPOSAL_IN_PROGRESS: {
    title: 'This application has not been submitted yet',
    cta: CONTINUE,
    target: 'proposal',
  },
  KYC_OTP_REQUIRED: {
    title: 'Enter the code we sent you',
    cta: CONTINUE,
    target: 'kyc',
  },
  KYC_IN_PROGRESS: {
    title: 'Verifying your identity',
    cta: CONTINUE,
    target: 'kyc',
  },
  KYC_PRICE_PENDING: {
    title: 'The premium for this plan has changed',
    cta: CONTINUE,
    target: 'kyc',
  },
  KYC_FAILED: {
    title: 'We couldn’t verify you with this insurer',
    cta: CONTINUE,
    target: 'kyc',
  },
  KYC_REQUIRED: {
    title: 'Complete your KYC to continue',
    cta: CONTINUE,
    target: 'kyc',
  },
  PLAN_COMMITTED: {
    title: 'Plan selected',
    cta: CONTINUE,
    target: 'proposal',
  },
  PLAN_CONFIGURING: {
    title: 'Finish setting up your plan',
    cta: CONTINUE,
    target: 'planSelection',
  },
  PLAN_LIST: {
    title: 'Choose a plan',
    cta: CONTINUE,
    target: 'plans',
  },
  QUOTE_IN_PROGRESS: {
    title: 'Fetching plans for you',
    cta: CONTINUE,
    target: 'plans',
  },
  QUOTE_UNAVAILABLE: {
    title: 'This quote is no longer available',
    cta: 'Start again',
    target: 'newQuote',
  },
};

const RESELECTION_STAGES: readonly TRhiV2JourneyStage[] = [
  'PLAN_LIST',
  'PLAN_CONFIGURING',
];

export const isKnownJourneyStage = (stage: string): stage is TRhiV2JourneyStage =>
  Object.prototype.hasOwnProperty.call(RHI_V2_JOURNEY_STAGES, stage);

const stageConfig = ({ stage }: IRhiV2Journey) =>
  isKnownJourneyStage(stage)
    ? RHI_V2_JOURNEY_STAGES[stage]
    : RHI_V2_JOURNEY_STAGES.PLAN_LIST;

const isAwaitingReselection = (journey: IRhiV2Journey) =>
  journey.needsReselection && RESELECTION_STAGES.includes(journey.stage);

export const getJourneyStatus = (journey: IRhiV2Journey) => {
  const config = stageConfig(journey);

  return isAwaitingReselection(journey)
    ? { ...config, title: 'Pick your plan again to continue' }
    : config;
};

const PLANS_PATH = RHI_V2_PLANS_PATH;

// Unpaid stages past the plan list; going back from a resume to any of them
// should land on the plan list rather than the home page.
const RETURNS_TO_PLAN_LIST: ReadonlySet<TRhiV2JourneyTarget> = new Set([
  'planSelection',
  'kyc',
  'proposal',
  'proposalReview',
  'payment',
]);

export const getJourneyPath = (journey: IRhiV2Journey): string | null => {
  const { target } = stageConfig(journey);
  const { quoteId, planId } = journey;

  if (target === 'support') return null;
  if (target === 'newQuote') return RHI_V2_NEW_QUOTE_PATH;
  if (target === 'plans' || !planId || isAwaitingReselection(journey)) return PLANS_PATH;

  const paths: Record<
    Exclude<TRhiV2JourneyTarget, 'support' | 'newQuote' | 'plans'>,
    string
  > = {
    planSelection: `${PLANS_PATH}/${planId}`,
    kyc: `${HEALTH_INSURANCE_BASE_PATH}/kyc/${planId}?quoteId=${quoteId}`,
    proposal: `${HEALTH_INSURANCE_BASE_PATH}/proposal/${planId}`,
    proposalReview: `${HEALTH_INSURANCE_BASE_PATH}/proposal/${planId}/review`,
    payment: `${HEALTH_INSURANCE_BASE_PATH}/payment/${planId}`,
    paymentStatus: `${HEALTH_INSURANCE_BASE_PATH}/success?quoteId=${quoteId}&planId=${planId}`,
    policy: `${HEALTH_INSURANCE_BASE_PATH}/policy/${planId}?quoteId=${quoteId}`,
  };

  return paths[target];
};

/** Whether backing out of this resumed journey should land on its plan list. */
export const returnsToPlanList = (journey: IRhiV2Journey) =>
  RETURNS_TO_PLAN_LIST.has(stageConfig(journey).target) &&
  getJourneyPath(journey) !== PLANS_PATH;

export const formatJourneyCover = ({ sumInsured }: IRhiV2Journey) => {
  if (sumInsured.unlimited) return 'Unlimited';
  return sumInsured.amount === null ? null : formatSumInsured(sumInsured.amount);
};

export const formatJourneyPlanName = (journey: IRhiV2Journey) =>
  [
    journey.createdByAgent ? 'Created by your advisor' : null,
    journey.plan?.name ?? 'Health insurance plan',
  ]
    .filter(Boolean)
    .join(' · ');

export const formatJourneySubtitle = (journey: IRhiV2Journey) => {
  const cover = formatJourneyCover(journey);
  return [formatJourneyPlanName(journey), cover ? `${cover} cover` : null]
    .filter(Boolean)
    .join(' · ');
};

export const formatJourneyMembers = ({ members }: IRhiV2Journey) =>
  members
    .map(({ relation }) => {
      const label = relation.replace(/_/g, ' ');
      return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
    })
    .join(', ');

export const formatJourneyUpdatedAt = ({ lastActivityAt }: IRhiV2Journey) =>
  new Date(lastActivityAt).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
  });

/** Words the card's expiresAt ("Expires in 3 days"); null, or already past, shows nothing. */
export const formatJourneyExpiry = (
  { expiresAt }: IRhiV2Journey,
  now: Date = new Date(),
): string | null => {
  if (!expiresAt) return null;
  const expires = parseISO(expiresAt);
  if (!isValid(expires) || expires <= now) return null;
  const days = differenceInCalendarDays(expires, now);
  if (days === 0) return 'Expires today';
  return days === 1 ? 'Expires tomorrow' : `Expires in ${days} days`;
};

interface IResumeReturn {
  /** `location.key` of the history entry the resume left from (the home page). */
  key: string;
  /** Where going back to that entry should land instead. */
  to: string;
}

const read = (): IResumeReturn | null => {
  try {
    const raw = sessionStorage.getItem(RHI_SESSION_KEYS.RESUME_RETURN);
    return raw ? (JSON.parse(raw) as IResumeReturn) : null;
  } catch {
    return null;
  }
};

/** Remember that going back to the entry with this key should open `to`. */
export const markResumeReturn = (key: string, to: string) => {
  try {
    const mark: IResumeReturn = { key, to };
    sessionStorage.setItem(RHI_SESSION_KEYS.RESUME_RETURN, JSON.stringify(mark));
  } catch {
    // Storage unavailable: back from the resumed page lands on home, as before.
  }
};

/** Where the entry with this key should send the customer, if a resume marked it. */
export const resumeReturnFor = (key: string): string | null => {
  const mark = read();
  return mark && mark.key === key ? mark.to : null;
};

export const clearResumeReturn = () => {
  try {
    sessionStorage.removeItem(RHI_SESSION_KEYS.RESUME_RETURN);
  } catch {
    // Nothing to clear.
  }
};
